import { app } from '@microsoft/teams-js';
import { useCallback, useEffect, useState } from 'react';
import { useBroadcastMessageReportPanelStyles } from './BroadcastMessageReportPanel.styles';
import {
    Button,
    Input,
    MessageBar,
    MessageBarActions,
    MessageBarBody,
    MessageBarGroup,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumnId,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Toolbar,
    ToolbarGroup,
    useTableColumnSizing_unstable,
    useTableFeatures,
    useTableSort,
} from '@fluentui/react-components';
import { useToolbarStyles } from '../../../../controls';
import {
    Dismiss24Regular,
    DismissRegular,
    Search24Regular,
} from '@fluentui/react-icons';
import { t } from 'i18next';
import { BroadcastMessageWithStatistic, ReportType } from '../../../../model';
import { useSelector } from 'react-redux';
import {
    broadcastMessageReport,
    broadcastMessageReportFilter,
    setCurrentPage,
    setFilter,
    setSorting,
} from '../../../../store/features/broadcastMessageReport/broadcastMessageReportSlice';
import { loadBroadcastMessages } from '../../../../store/features/broadcastMessageReport/broadcastMessageReportSlice.thunks';
import { useAppDispatch } from '../../../../store/hooks';
import { useDebouncedCallback } from 'use-debounce';
import PaginationBar from '../../../PaginationBar';
import { createReport } from '../../../../store/features/reporting/reportingAPI';
import {
    BroadcastMessageColumnId,
    broadcastMessageListColumnIds,
    BroadcastMessageReportPanelMode,
    useBroadcastMessageReportPanelColumns,
} from './BroadcastMessageReportPanel.utils';
import BroadcastMessageVersionsDrawer from './BroadcastMessageVersionsDrawer/BroadcastMessageVersionsDrawer';
import { ColumnSizingOptions } from '../../../../utils/tableUtils';

const columnSizingOptions: ColumnSizingOptions<BroadcastMessageColumnId> = {
    title: {
        minWidth: 120,
    },
    content: {
        minWidth: 230,
        autoFitColumns: true,
    },
    createdOnUtc: {
        minWidth: 100,
    },
    'stats/totalCount': {
        minWidth: 300,
        autoFitColumns: true,
    },
};

export interface BroadcastMessageReportPanelProps {
    mode?: BroadcastMessageReportPanelMode;
    onRowClick?: (msg: BroadcastMessageWithStatistic) => void;
}

const BroadcastMessageReportPanel: React.FC<
    BroadcastMessageReportPanelProps
> = ({ mode = 'full', onRowClick }) => {
    const dispatch = useAppDispatch();
    const styles = useBroadcastMessageReportPanelStyles();
    const toolbarStyles = useToolbarStyles();

    const { items, pagination, sorting, loadStatus } = useSelector(
        broadcastMessageReport
    );
    const { queryText } = useSelector(broadcastMessageReportFilter);

    const [searchQuery, setSearchQuery] = useState<string>('');

    const [selectedBroadcastId, setSelectedBroadcastId] = useState<
        string | null
    >(null);
    const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);

    const openHistoryDrawer = useCallback((messageId: string) => {
        setSelectedBroadcastId(messageId);
        setHistoryDrawerOpen(true);
    }, []);

    const [
        sentBroadcastMessageReportRequests,
        setSentBroadcastMessageReportRequests,
    ] = useState<string[]>([]);

    const onSearchQueryUpdateCallback = useDebouncedCallback(
        (query: string) => {
            dispatch(
                setFilter({
                    queryText: query,
                })
            );
            dispatch(loadBroadcastMessages());
        },
        300
    );

    const onClearSearchTextClick = useCallback(() => {
        if (!!queryText) {
            onSearchQueryUpdateCallback.cancel();
            dispatch(
                setFilter({
                    queryText: '',
                })
            );
            dispatch(setCurrentPage(1));
            dispatch(loadBroadcastMessages());
        }
    }, [dispatch, onSearchQueryUpdateCallback, queryText]);

    useEffect(() => {
        app.notifySuccess();
    }, []);

    useEffect(() => {
        dispatch(loadBroadcastMessages());
    }, [dispatch]);

    useEffect(() => {
        onSearchQueryUpdateCallback(searchQuery);
    }, [searchQuery, onSearchQueryUpdateCallback]);

    const dismissMessage = (broadcastMessageId: string) =>
        setSentBroadcastMessageReportRequests((prev) =>
            prev.filter((x) => x !== broadcastMessageId)
        );

    const generateReportCallback = useCallback(
        async (broadcastMessageId: string) => {
            var success = await createReport({
                type: ReportType.BroadcastMessagingReport,
                broadcastMessageId,
            });

            if (!success) {
                return;
            }

            setSentBroadcastMessageReportRequests((prev) => [
                ...prev,
                broadcastMessageId,
            ]);

            setTimeout(() => {
                dismissMessage(broadcastMessageId);
            }, 3000);
        },
        []
    );

    const columns = useBroadcastMessageReportPanelColumns(mode, {
        generateReport: generateReportCallback,
        openDrawer: openHistoryDrawer,
    });

    const {
        getRows,
        sort: { getSortDirection },
        columnSizing_unstable: columnSizing,
    } = useTableFeatures(
        {
            getRowId: (item) => item.id,
            columns,
            items: items ?? [],
        },
        [
            useTableSort({
                sortState: {
                    sortColumn: sorting.sortBy,
                    sortDirection: sorting.sortByDescending
                        ? 'ascending'
                        : 'descending',
                },
            }),
            useTableColumnSizing_unstable({
                columnSizingOptions,
                autoFitColumns: false,
            }),
        ]
    );

    const headerSortProps = useCallback(
        (columnId: TableColumnId) => ({
            onClick: () => {
                if (columnId === broadcastMessageListColumnIds.actions) {
                    return;
                }
                dispatch(setSorting(columnId as string));
                dispatch(loadBroadcastMessages());
            },
            sortDirection: getSortDirection(columnId),
        }),
        [getSortDirection, dispatch]
    );

    const rows = getRows();

    return (
        <div className={styles.root}>
            <Toolbar aria-label="Flights" className={toolbarStyles.toolbar}>
                <ToolbarGroup role="presentation"></ToolbarGroup>
                <ToolbarGroup role="presentation">
                    <Input
                        className={toolbarStyles.searchBar}
                        placeholder={t('SearchFlightsInputPlaceholder')!}
                        size="small"
                        contentAfter={
                            <Button
                                appearance="transparent"
                                onClick={onClearSearchTextClick}
                                icon={
                                    !queryText ? (
                                        <Search24Regular />
                                    ) : (
                                        <Dismiss24Regular />
                                    )
                                }
                            />
                        }
                        onChange={(_, data) => {
                            setSearchQuery(data.value ?? '');
                        }}
                        value={searchQuery}
                    />
                </ToolbarGroup>
            </Toolbar>
            <MessageBarGroup animate="both" className={styles.messageGroup}>
                {sentBroadcastMessageReportRequests.map(
                    (broadcastMessageId) => (
                        <MessageBar
                            key={broadcastMessageId}
                            intent="info"
                            layout="multiline"
                        >
                            <MessageBarBody>
                                {t(
                                    'NotificationMessage_OnBroadcastMessageReportRequest'
                                )}
                            </MessageBarBody>
                            <MessageBarActions
                                containerAction={
                                    <Button
                                        onClick={() =>
                                            dismissMessage(broadcastMessageId)
                                        }
                                        aria-label="dismiss"
                                        appearance="transparent"
                                        icon={<DismissRegular />}
                                    />
                                }
                            />
                        </MessageBar>
                    )
                )}
            </MessageBarGroup>
            <Table
                className={styles.table}
                size="small"
                role="grid"
                aria-label="broadcastMessages"
                noNativeElements
                sortable
            >
                <TableHeader>
                    <TableRow className={styles.tableRow}>
                        {columns.map((column) => (
                            <TableHeaderCell
                                key={column.columnId}
                                style={
                                    columnSizing.getTableHeaderCellProps(
                                        column.columnId
                                    )?.style
                                }
                                {...headerSortProps(column.columnId)}
                            >
                                {column.renderHeaderCell()}
                            </TableHeaderCell>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map(({ item }) => (
                        <TableRow
                            className={styles.tableRow}
                            key={item.id}
                            onClick={() => onRowClick?.(item)}
                        >
                            {columns.map((column) => (
                                <TableCell
                                    key={column.columnId}
                                    tabIndex={0}
                                    role="gridcell"
                                    style={{
                                        ...columnSizing.getTableCellProps(
                                            column.columnId
                                        )?.style,
                                        justifyContent: 'center',
                                    }}
                                >
                                    {column.renderCell(item)}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <BroadcastMessageVersionsDrawer
                open={historyDrawerOpen}
                messageId={selectedBroadcastId}
                onOpenChange={setHistoryDrawerOpen}
            />
            <PaginationBar
                currentPage={pagination.currentPage}
                pageSize={pagination.pageSize}
                totalItems={pagination.totalCount}
                onChange={(page) => {
                    onSearchQueryUpdateCallback.cancel();
                    dispatch(setCurrentPage(page));
                    dispatch(loadBroadcastMessages());
                }}
            />
            {loadStatus === 'loading' && (
                <Spinner className={styles.spinner} appearance="primary" />
            )}
        </div>
    );
};

export default BroadcastMessageReportPanel;
